import React from 'react';
import { View, Text, TouchableOpacity, StyleSheet, ActivityIndicator } from 'react-native';
import { PRIMITIVE_COLORS, SEMANTIC_COLORS } from '@/constants/residents/residentColors';
import { RESIDENTS_SVG_COMPONENTS } from '@/assets/residents';

interface DesktopMaintenanceTileProps {
  openTicketsCount: number;
  isLoading?: boolean;
  onPress: () => void;
}

/**
 * DesktopMaintenanceTile - Maintenance requests tile for desktop home dashboard
 */
export const DesktopMaintenanceTile: React.FC<DesktopMaintenanceTileProps> = ({
  openTicketsCount,
  isLoading = false,
  onPress,
}) => {
  const hasOpenTickets = openTicketsCount > 0;

  const getStatusText = (): string => {
    if (openTicketsCount === 0) return 'No open requests';
    if (openTicketsCount === 1) return '1 open request';
    return `${openTicketsCount} open requests`;
  };

  return (
    <TouchableOpacity style={styles.tile} onPress={onPress} activeOpacity={0.8}>
      <View style={styles.header}>
        <View style={styles.iconContainer}>
          <RESIDENTS_SVG_COMPONENTS.WRENCH width={24} height={24} color={PRIMITIVE_COLORS.resioBlue} />
        </View>
        <Text style={styles.title}>Maintenance</Text>
      </View>

      <View style={styles.content}>
        {isLoading ? (
          <ActivityIndicator size="small" color={SEMANTIC_COLORS.text.tertiary} />
        ) : (
          <>
            <Text style={styles.count}>{openTicketsCount}</Text>
            <Text style={styles.status}>{getStatusText()}</Text>
          </>
        )}
      </View>

      <TouchableOpacity
        style={styles.actionButton}
        onPress={(e) => {
          e.stopPropagation();
          onPress();
        }}
        activeOpacity={0.8}
      >
        <Text style={styles.actionButtonText}>
          {hasOpenTickets ? 'View requests' : 'New request'}
        </Text>
      </TouchableOpacity>
    </TouchableOpacity>
  );
};

const styles = StyleSheet.create({
  tile: {
    backgroundColor: PRIMITIVE_COLORS.gray900,
    borderRadius: 16,
    padding: 20,
    flex: 1,
    minHeight: 160,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    marginBottom: 16,
  },
  iconContainer: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: PRIMITIVE_COLORS.white10,
    justifyContent: 'center',
    alignItems: 'center',
  },
  title: {
    fontSize: 16,
    fontWeight: '600',
    color: SEMANTIC_COLORS.text.primary,
  },
  content: {
    flex: 1,
  },
  count: {
    fontSize: 28,
    fontWeight: '700',
    color: SEMANTIC_COLORS.text.primary,
    marginBottom: 4,
  },
  status: {
    fontSize: 14,
    color: SEMANTIC_COLORS.text.tertiary,
  },
  actionButton: {
    backgroundColor: PRIMITIVE_COLORS.resioBlue,
    borderRadius: 8,
    paddingVertical: 10,
    paddingHorizontal: 16,
    alignSelf: 'flex-start',
    marginTop: 16,
  },
  actionButtonText: {
    fontSize: 13,
    fontWeight: '600',
    color: SEMANTIC_COLORS.text.primary,
  },
});

export default DesktopMaintenanceTile;
